import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", sku: "", brand_id: "", category_id: "", images: [], condition: "New", description: "", specifications: {}, availability: "check", price_display_type: "request", featured: false, new_arrival: false, offer: false, tags: [], published: true };

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/products/admin/all").then(r => setItems(r.data));
    api.get("/categories").then(r => setCats(r.data));
    api.get("/brands/admin/all").then(r => setBrands(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...editing };
      if (typeof payload.tags === "string") payload.tags = payload.tags.split(",").map(t=>t.trim()).filter(Boolean);
      if (typeof payload.images === "string") payload.images = payload.images.split(",").map(t=>t.trim()).filter(Boolean);
      if (typeof payload.specifications === "string") try { payload.specifications = JSON.parse(payload.specifications || "{}"); } catch { payload.specifications = {}; }
      if (payload.id) await api.put(`/products/${payload.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error("Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/products/${id}`); toast.success("Deleted"); load(); };

  const uploadImage = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
    setEditing(e => ({ ...e, images: [...(Array.isArray(e.images) ? e.images : []), url] }));
    toast.success("Image uploaded");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-extrabold text-2xl">Products</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-orange" onClick={() => setEditing({ ...EMPTY })} data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1"/> Add Product</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle>{editing?.id ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
            {editing && (
              <div className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Name *</Label><Input value={editing.name} onChange={e=>setEditing({...editing, name: e.target.value})}/></div>
                  <div><Label>SKU</Label><Input value={editing.sku||""} onChange={e=>setEditing({...editing, sku: e.target.value})}/></div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><Label>Category</Label>
                    <Select value={editing.category_id||""} onValueChange={v=>setEditing({...editing, category_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                      <SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Brand</Label>
                    <Select value={editing.brand_id||""} onValueChange={v=>setEditing({...editing, brand_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                      <SelectContent>{brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Condition</Label>
                    <Select value={editing.condition} onValueChange={v=>setEditing({...editing, condition: v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent><SelectItem value="New">New</SelectItem><SelectItem value="Refurbished">Refurbished</SelectItem><SelectItem value="Used">Used</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Description</Label><Textarea rows={3} value={editing.description||""} onChange={e=>setEditing({...editing, description: e.target.value})}/></div>
                <div><Label>Specifications (JSON)</Label><Textarea rows={3} value={typeof editing.specifications === "string" ? editing.specifications : JSON.stringify(editing.specifications || {}, null, 2)} onChange={e=>setEditing({...editing, specifications: e.target.value})}/></div>
                <div><Label>Tags (comma separated)</Label><Input value={Array.isArray(editing.tags) ? editing.tags.join(", ") : (editing.tags||"")} onChange={e=>setEditing({...editing, tags: e.target.value})}/></div>
                <div className="border border-[#E2E8F0] rounded-md p-3 space-y-2 bg-[#F8FAFC]">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">SEO</div>
                  <div><Label>URL Slug</Label><Input data-testid="product-slug" value={editing.slug||""} placeholder="auto-generated from name" onChange={e=>setEditing({...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-")})}/></div>
                  <div><Label>SEO Title <span className="text-slate-400 text-xs">({(editing.seo_title||"").length}/60)</span></Label><Input data-testid="product-seo-title" value={editing.seo_title||""} onChange={e=>setEditing({...editing, seo_title: e.target.value})}/></div>
                  <div><Label>Meta Description <span className="text-slate-400 text-xs">({(editing.seo_description||"").length}/160)</span></Label><Textarea rows={2} data-testid="product-seo-desc" value={editing.seo_description||""} onChange={e=>setEditing({...editing, seo_description: e.target.value})}/></div>
                </div>
                <div>
                  <Label>Images</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(Array.isArray(editing.images) ? editing.images : []).map((url,i)=>(
                      <div key={i} className="relative w-20 h-20"><img src={url} alt="" className="w-full h-full object-cover rounded"/></div>
                    ))}
                  </div>
                  <Input placeholder="Or paste image URL (comma separated)" value={Array.isArray(editing.images) ? editing.images.join(", ") : (editing.images||"")} onChange={e=>setEditing({...editing, images: e.target.value})}/>
                  <div className="mt-2"><input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadImage(e.target.files[0])}/></div>
                </div>
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2"><Switch checked={!!editing.featured} onCheckedChange={v=>setEditing({...editing, featured: v})}/> Featured</label>
                  <label className="flex items-center gap-2"><Switch checked={!!editing.new_arrival} onCheckedChange={v=>setEditing({...editing, new_arrival: v})}/> New Arrival</label>
                  <label className="flex items-center gap-2"><Switch checked={!!editing.offer} onCheckedChange={v=>setEditing({...editing, offer: v})}/> Offer</label>
                  <label className="flex items-center gap-2"><Switch checked={!!editing.published} onCheckedChange={v=>setEditing({...editing, published: v})}/> Published</label>
                </div>
                <Button className="btn-orange w-full" onClick={save}>Save</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-left"><tr><th className="p-3">Product</th><th>Category</th><th>Brand</th><th>Cond.</th><th>Flags</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-t border-[#E2E8F0]" data-testid={`admin-product-${p.id}`}>
                <td className="p-3 flex items-center gap-3">{p.images?.[0] && <img src={p.images[0]} className="w-10 h-10 rounded object-cover"/>}<div><div className="font-semibold">{p.name}</div><div className="text-xs text-slate-500">{p.sku}</div></div></td>
                <td>{cats.find(c=>c.id===p.category_id)?.name || "-"}</td>
                <td>{brands.find(b=>b.id===p.brand_id)?.name || "-"}</td>
                <td>{p.condition}</td>
                <td className="space-x-1">{p.featured && <Badge>F</Badge>}{p.new_arrival && <Badge className="bg-[#1E3A8A]">N</Badge>}{p.offer && <Badge className="bg-emerald-600">O</Badge>}</td>
                <td>{p.published ? <Badge className="bg-emerald-600">Live</Badge> : <Badge variant="outline">Draft</Badge>}</td>
                <td className="pr-3 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={()=>{ setEditing({...p, specifications: JSON.stringify(p.specifications||{}, null, 2), tags: (p.tags||[]).join(", "), images: (p.images||[]).join(", ")}); setOpen(true); }}><Edit className="w-4 h-4"/></Button>
                  <Button size="icon" variant="ghost" onClick={()=>remove(p.id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
